import avatar4 from "../../../assets/img/avatars/avatar4.png";
import React, { useEffect, useMemo } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useClerkAuth } from "../../../hooks/useClerkAuth";
import LoadingScreen from "../../../components/Loading/LoadingScreen";
import GeneralSection from "./components/GeneralSection";
import Devices from "./components/Devices";
import SignInMethods from "./components/SignInMethods";
import BrokerIntegrationSection from "./components/BrokerIntegrationSection";
import DisplayPreferences from "./components/DisplayPreferences";
import ProfileBillingTab from "./components/ProfileBillingTab";
import PnlMilestonesSettings from "./components/PnlMilestonesSettings";
import InstrumentMarketDataSettings from "./components/InstrumentMarketDataSettings";
import NotificationSettings from "./components/NotificationSettings";
import {
  accountSettingsPathFromLocation,
  accountSettingsTabPath,
  ACCOUNT_SETTINGS_DEFAULT_TAB,
  isKnownAccountSettingsTabId,
  resolveAccountSettingsTabId,
  type AccountSettingsTabId,
} from "@/constants/accountSettingsSidebar";
import { NeedHelpButton } from "@/components/need-help-button";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import { useShadcnInsetHeaderActions } from "@/contexts/ShadcnInsetHeaderActionsContext";
import { useBillingContext } from "@/contexts/BillingContext";
import { isProfileTabAllowedWithoutTier1 } from "@/utils/billingAllowlist";
import { cn } from "@/lib/utils";

/** Help centre article per settings tab; omitted tabs show no breadcrumb help button. */
const ACCOUNT_SETTINGS_HELP_URLS: Partial<
  Record<AccountSettingsTabId, { href: string; label?: string }>
> = {
  general: { href: "https://help.tesseratrades.com/account" },
  billing: { href: "https://help.tesseratrades.com/billing" },
  "instrument-market-data": {
    href: "https://help.tesseratrades.com/brokers-and-data/instruments",
    label: "Explore instruments",
  },
};

function Profile() {
  const location = useLocation();
  const history = useHistory();
  const shadcnInset = useShadcnInsetChrome();
  const { setActions: setInsetHeaderActions } = useShadcnInsetHeaderActions();
  const { tabId: tabParam } = useParams<{ tabId?: string }>();
  const { isLoading: billingLoading, isTier1Granted } = useBillingContext();

  const activeTab = useMemo(
    () => resolveAccountSettingsTabId(location.pathname, location.search),
    [location.pathname, location.search]
  );

  useEffect(() => {
    // Broker Management renders its own breadcrumb toolbar (Connect new broker + Need Help).
    if (activeTab === "broker-api") {
      return;
    }
    const help = ACCOUNT_SETTINGS_HELP_URLS[activeTab];
    if (!shadcnInset || !help) {
      setInsetHeaderActions(null);
      return;
    }
    setInsetHeaderActions(<NeedHelpButton href={help.href} label={help.label} />);
    return () => setInsetHeaderActions(null);
  }, [activeTab, setInsetHeaderActions, shadcnInset]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.has("tab")) {
      history.replace(accountSettingsPathFromLocation(location.pathname, location.search));
      return;
    }
    if (tabParam && !isKnownAccountSettingsTabId(tabParam)) {
      history.replace(accountSettingsTabPath(ACCOUNT_SETTINGS_DEFAULT_TAB));
      return;
    }
    // Unsubscribed users may only open general / authentication / billing.
    if (
      !billingLoading &&
      !isTier1Granted &&
      !isProfileTabAllowedWithoutTier1(activeTab)
    ) {
      history.replace(accountSettingsTabPath(ACCOUNT_SETTINGS_DEFAULT_TAB));
    }
  }, [
    activeTab,
    billingLoading,
    history,
    isTier1Granted,
    location.pathname,
    location.search,
    tabParam,
  ]);

  const { isLoaded, user } = useClerkAuth();

  if (!isLoaded) {
    return <LoadingScreen message="Loading profile..." />;
  }

  const avatarImage = user?.imageUrl || avatar4;

  const renderTabContent = () => {
    const gatedAwayFromTab =
      !billingLoading &&
      !isTier1Granted &&
      !isProfileTabAllowedWithoutTier1(activeTab);
    const tab = gatedAwayFromTab ? ACCOUNT_SETTINGS_DEFAULT_TAB : activeTab;

    switch (tab) {
      case "general":
        return <GeneralSection avatarImage={avatarImage} />;
      case "authentication":
        return <SignInMethods />;
      case "billing":
        return <ProfileBillingTab />;
      case "display-preferences":
        return <DisplayPreferences />;
      case "notifications":
        return <NotificationSettings />;
      case "milestones":
        return <PnlMilestonesSettings />;
      case "broker-api":
        return <BrokerIntegrationSection />;
      case "instrument-market-data":
        return <InstrumentMarketDataSettings />;
      case "devices":
        return <Devices title="Active Devices" />;
      default:
        return <GeneralSection avatarImage={avatarImage} />;
    }
  };

  return (
    <div
      data-account-settings-page=""
      className={cn(
        "flex w-full min-w-0 flex-col overflow-x-hidden",
        !shadcnInset && "pt-[100px] md:pt-[80px] lg:pt-10"
      )}
    >
      {renderTabContent()}
    </div>
  );
}

export default Profile;
